// pillColors.js — the canonical Segment-Title pill colour recipe for the v1 D3 wheel engine.
//
// SINGLE SOURCE OF TRUTH, ported verbatim from the design deck's
// `design-handoff/harness/elevations/segment-title-pill.mjs` `colorsFor()` (that file's
// lines 98-103) — the Gordon design law, client-confirmed against a reference screenshot
// 2026-07-05 and re-confirmed 2026-07-06 ("recolour-alone was rejected; the legs must go"):
//   · pill BORDER (edge) = the wedge's own hue, darkened ~34% toward black ONLY when the hue
//                          itself is too light to read as a keyline on a near-white pill.
//   · pill FILL   (tint) = that same hue mixed 90% toward white (a FAINT tint — NEVER solid).
//   · pill TEXT          = canonical ink #123051 (applied by the caller, not here).
// This is the exact math the rim-hug on-brand transform (ewflow-onbrand.mjs) bakes into every
// pill. Consumed by createPie.js ONLY when WHEEL_PILL_LABELS_ENABLED is on. Pure + unit-tested
// (pillColors.test.js) so the recipe can never silently drift from the deck.

export const PILL_INK = "#123051";

// Normalise "rgb(r,g,b)" | "rgba(...)" | "#rgb" | "#rrggbb" -> "#rrggbb" (lowercase), else null.
export function toHex(c) {
  if (!c) return null;
  c = ("" + c).trim();
  if (c.charAt(0) === "#") {
    if (c.length === 4) c = "#" + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
    return c.length >= 7 ? c.slice(0, 7).toLowerCase() : null;
  }
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (!m) return null;
  const p = m[1].split(",");
  if (p.length < 3) return null;
  const r = parseInt(p[0], 10);
  const g = parseInt(p[1], 10);
  const b = parseInt(p[2], 10);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return "#" + (0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

// WCAG relative luminance — decides whether a hue is too light to read as its own keyline.
export function iyLum(f) {
  try {
    const n = parseInt(f.slice(1), 16);
    const q = (v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * q(n >> 16) + 0.7152 * q((n >> 8) & 255) + 0.0722 * q(n & 255);
  } catch (e) {
    return 0.4;
  }
}

// Linear RGB mix: f -> g by fraction t (0..1), rounded per channel.
export function iyMix(f, g, t) {
  const a = parseInt(f.slice(1), 16);
  const b = parseInt(g.slice(1), 16);
  const r = Math.round(((a >> 16) & 255) * (1 - t) + ((b >> 16) & 255) * t);
  const gg = Math.round(((a >> 8) & 255) * (1 - t) + ((b >> 8) & 255) * t);
  const bb = Math.round((a & 255) * (1 - t) + (b & 255) * t);
  return "#" + (0x1000000 + (r << 16) + (gg << 8) + bb).toString(16).slice(1);
}

// The canonical Segment-Title pill colours for a given wedge hue (any CSS hex/rgb string).
// Returns { edge, tint, tintD } — the exact values segment-title-pill.mjs / ewflow-onbrand.mjs
// bake into every pill (tintD = the dark-theme twin; unused by the light-only D3 wheel but kept
// for parity with the source). Falls back gracefully (edge/tint = the raw input) if the hue can't
// be parsed, so a malformed colour never throws inside the render loop.
export function colorsFor(hue) {
  const hex = toHex(hue);
  if (!hex) return { edge: hue, tint: hue, tintD: hue };
  const edge = iyLum(hex) > 0.62 ? iyMix(hex, "#000000", 0.34) : hex;
  const tint = iyMix(hex, "#ffffff", 0.9);
  const tintD = iyMix(hex, "#12141b", 0.82);
  return { edge, tint, tintD };
}

// ISP-9999 gallery-pill-gif: the on-hover TOOLTIP pill paint (Gallery/Explore/
// every card-hover single-segment-name popup, D3Wheels/createPie.js's shared
// `.tooltip` div) — the SAME colorsFor() recipe as the rim-pill labels, with a
// safe neutral fallback (#c9c6c5, the tooltip's pre-existing default) when no
// hue is available. ONE function so createPie.js's two hover call sites
// (arc-hover mousemove + label-text-hover mousemove) can never drift apart.
export function tooltipPillPaint(hue) {
  const pc = colorsFor(hue || "#c9c6c5");
  return { background: pc.tint, border: pc.edge, ink: PILL_INK };
}
